import React from 'react';
import { NavLink } from 'react-router-dom';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';

/* El lateral se achica a íconos y se vuelve a abrir al pasar el mouse.
   El estado fijo queda guardado; el de hover no.

   Ojo con una trampa: si la apertura por hover se hace con CSS, al tocar
   "achicar" el puntero queda encima del menú y lo vuelve a abrir en el acto,
   así que parece que el botón no hace nada. Por eso todo se resuelve acá,
   y el hover queda bloqueado hasta que el mouse sale del menú. */

const LLAVE = 'nav_colapsada';
export const ANCHO = 250;
export const ANCHO_CHICO = 72;

export function useLateral() {
  const [colapsado, setColapsado] = React.useState(() => {
    try { return localStorage.getItem(LLAVE) === '1'; } catch { return false; }
  });
  const [sobre, setSobre] = React.useState(false);
  const [trabado, setTrabado] = React.useState(false);

  const alternar = () => setColapsado(v => {
    try { localStorage.setItem(LLAVE, v ? '0' : '1'); } catch { /* modo privado */ }
    setSobre(false);
    setTrabado(true);       // hasta que el mouse se vaya, el hover no manda
    return !v;
  });

  const abierto = !colapsado || (sobre && !trabado);

  // la campana de avisos está anclada a la izquierda: tiene que seguir al menú
  React.useEffect(() => {
    document.documentElement.style.setProperty('--ancho-lateral', (colapsado ? ANCHO_CHICO : ANCHO) + 'px');
  }, [colapsado]);

  return {
    colapsado, abierto, alternar,
    props: {
      className: 'barra-lateral' + (colapsado ? ' colapsada' : ''),
      style: { width: colapsado ? ANCHO_CHICO : ANCHO },
      onMouseEnter: () => { if (!trabado) setSobre(true); },
      onMouseLeave: () => { setSobre(false); setTrabado(false); },
    },
    /* el ancho real del panel: lo aplica cada layout sobre su <aside> */
    ancho: abierto ? ANCHO : ANCHO_CHICO,
  };
}

/* Un ítem del menú. Achicado es un cuadrado redondeado con el ícono al medio
   y el nombre como tooltip. */
export function ItemNav({ to, end, icono: Icono, label, abierto, onClick }) {
  return (
    <NavLink to={to} end={end} onClick={onClick} title={abierto ? undefined : label}
      className={({ isActive }) => 'nav-item' + (isActive ? ' activo' : '') + (abierto ? '' : ' solo-icono')}>
      <Icono size={19} />
      {abierto && <span className="nav-texto">{label}</span>}
    </NavLink>
  );
}

export function BotonColapsar({ colapsado, onClick, abierto }) {
  const Icono = colapsado ? PanelLeftOpen : PanelLeftClose;
  return (
    <button onClick={onClick} className={'nav-item accion' + (abierto ? '' : ' solo-icono')}
      title={colapsado ? 'Fijar el menú abierto' : 'Achicar el menú a íconos'}>
      <Icono size={19} />
      {abierto && <span className="nav-texto">{colapsado ? 'Fijar abierto' : 'Achicar menú'}</span>}
    </button>
  );
}

/* Degradé de marca a partir del color del cliente: oscuro arriba, el color
   abajo y un brillo suave al pie. Con un solo color queda plano; la variante
   en la misma escala es la que le da profundidad. */
function aRgb(h) {
  h = String(h || '').replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  const n = parseInt(h, 16);
  return Number.isNaN(n) ? [46, 125, 226] : [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
/* p > 0 aclara, p < 0 oscurece. El alfa va acá adentro: pegarle "55" a un
   rgb(...) da un color inválido y el navegador se come el degradé entero. */
const mezcla = (h, p, alfa = 1) => {
  const t = p < 0 ? 0 : 255, a = Math.abs(p);
  const [r, g, b] = aRgb(h).map(v => Math.round((t - v) * a + v));
  return `rgba(${r},${g},${b},${alfa})`;
};

export function degradeDeMarca(color) {
  const c = color && String(color).trim() ? (String(color).trim()[0] === '#' ? color : '#' + color) : '#2E7DE2';
  return [
    `radial-gradient(130% 70% at 12% 102%, ${mezcla(c, 0.34, 0.38)} 0%, transparent 62%)`,
    `radial-gradient(90% 55% at 100% 0%, ${mezcla(c, -0.30, 0.55)} 0%, transparent 70%)`,
    `linear-gradient(162deg, ${mezcla(c, -0.80)} 0%, ${mezcla(c, -0.45)} 52%, ${mezcla(c, 0)} 100%)`,
  ].join(', ');
}

/* Los colores del menú cuando el fondo es el degradé */
export const SOBRE_DEGRADE = {
  '--nav-color': 'rgba(255,255,255,0.80)',
  '--nav-fuerte': '#fff',
  '--nav-hover': 'rgba(255,255,255,0.13)',
  '--nav-activo-fondo': '#fff',
  '--nav-activo-texto': '#0d1b2e',
};
